import { type AssetPosition, type AssetType, assetTypeEnum } from "kubb";
import * as Yup from "yup";
import {
  yupRequiredString,
  yupString,
} from "../components/Forms/yupValidators";
import type { Asset } from "../utils/object.tsx";
import { nameRegEx } from "./utils/schema";

export const groupValidation = Yup.string()
  .notRequired()
  .matches(/^\/?[\wÀ-ɏ\-_.,'():&\[\]+]+(\/[\wÀ-ɏ\-_.,'():&\[\]+]+)*\/?$/u, {
    message:
      "Only letters, numbers, and the following punctuation are allowed: -_.,'():&[]+/ (use / for group tiers)",
  })
  .test(
    "max-tiers",
    "Maximum of 8 group tiers allowed",
    (v) => !v || v.replace(/^\/|\/$/g, "").split("/").length <= 8,
  )
  .test(
    "reserved-name",
    "'NONE' is a reserved name and cannot be used as a group",
    (v) => !v || v.replace(/^\/|\/$/g, "").toUpperCase() !== "NONE",
  );

export const assetPositionSchema = {
  asset_position_type: yupRequiredString.notOneOf(["undefined", ""]),
  asset_position_reference: yupString.notRequired().matches(...nameRegEx),
  parent_asset_position_id: yupString,
  group: groupValidation,
};

export const BillableAssets: AssetType[] = [
  assetTypeEnum.heat_meter,
  assetTypeEnum.cooling_meter,
  assetTypeEnum.heating_and_cooling_meter,
  assetTypeEnum.electricity_meter,
  assetTypeEnum.gas_meter,
  assetTypeEnum.water_meter,
];

export const NonBillableAssetTypes: AssetType[] = [
  assetTypeEnum.input_output_module,
  assetTypeEnum.room_sensor,
  assetTypeEnum.co2_room_sensor,
];

export const InfrastructureAssetTypes: AssetType[] = [
  assetTypeEnum.wireless_cluster,
  assetTypeEnum.wireless_receiver,
  assetTypeEnum.wireless_repeater,
  assetTypeEnum.gateway,
  assetTypeEnum.sim_card,
];

export const NonInfrastructureAssetTypes: AssetType[] = [
  ...BillableAssets,
  ...NonBillableAssetTypes,
];

/**
 * Maps a position type to the full set of asset types that can be assigned to it.
 * Mirrors PERMITTED_TYPE_MATCHES from management-api/src/asset_register/domain/model/asset_position/link.py
 */
export function getCompatibleAssetTypes(positionType: AssetType): AssetType[] {
  switch (positionType) {
    case assetTypeEnum.heat_meter:
      return [
        assetTypeEnum.heat_meter,
        assetTypeEnum.heating_and_cooling_meter,
      ];
    case assetTypeEnum.cooling_meter:
      return [
        assetTypeEnum.cooling_meter,
        assetTypeEnum.heat_meter,
        assetTypeEnum.heating_and_cooling_meter,
      ];
    default:
      return [positionType];
  }
}

export function isAsset(asset: Asset | AssetPosition): asset is Asset {
  return (asset as Asset)?.asset_type !== undefined;
}
export function isAssetPosition(
  asset: Asset | AssetPosition,
): asset is AssetPosition {
  return (asset as AssetPosition)?.asset_position_type !== undefined;
}
